/**
 * cart-utils.js — stateless helper functions for the Spicers cart pages.
 *
 * Environment notes:
 *  - Served as a plain `<script defer>` asset (no bundler), loaded BEFORE
 *    cart.js so these functions are available as ordinary globals to the cart
 *    orchestration code. (Mirrors how the rest of the theme shares helpers.)
 *  - Pure/DOM-read helpers only — no cart mutation (see cart-persist.js for
 *    the /cart AJAX routine) and no page wiring (that stays in cart.js).
 */

// Shopify variant IDs for the shipping line items added at checkout.
const SHIPPING_GB_VARIANT_ID = 39374699167875;
const SHIPPING_NI_VARIANT_ID = 39374699298947;
// Shipping code carried on the shipping line items added to the cart.
const SHIPPING_CODE_ND = "ND";
// Cart-level attributes set whenever the cart is rebuilt.
const CART_ATTRIBUTES = { address_checked: true, site_id: "spicers" };

// --- Cart AJAX --------------------------------------------------------------

// POST a JSON body to a /cart/*.js endpoint. On failure, surface Shopify's
// human-readable error (matching the old `$.ajax` error callbacks) and throw
// so the caller's async flow aborts. Resolves to the parsed JSON response.
async function postCartJSON(url, body) {
  const config = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  };
  if (body !== undefined) config.body = JSON.stringify(body);

  const response = await fetch(url, config);
  if (!response.ok) {
    try {
      const err = await response.json();
      if (err && err.description) alert(err.description);
    } catch (e) {
      /* non-JSON / empty body — nothing to surface */
    }
    throw new Error(`Cart request failed: ${url} (${response.status})`);
  }
  return response.json().catch(() => ({}));
}

// --- Postcode helpers -------------------------------------------------------

function getOutcode(postcode) {
  const cleaned = postcode.trim().replace(/\s/g, "");
  const regex = /^(GIR|[A-Z]{1,2}[0-9]{1,2}(?:[A-Z])?|[A-Z][0-9]{1,2}(?:[A-Z])?)(?=[0-9][A-Z]{2})/i;
  const match = cleaned.match(regex);
  return match ? match[1] : [];
}

function longShippingTime(postcode) {
  const postCodes = "KA27,KA28,AB31,AB33,AB34,AB35,AB36,AB37,AB38,AB40,AB41,AB42,AB43,AB44,AB45,AB46,AB47,AB48,AB49,AB50,AB51,AB52,AB53,AB54,AB55,AB56,IV1,IV2,IV3,IV4,IV5,IV6,IV7,IV8,IV9,IV10,IV11,IV12,IV13,IV14,IV15,IV16,IV17,IV18,IV19,IV20,IV21,IV22,IV23,IV24,IV25,IV26,IV27,IV28,IV29,IV30,IV31,IV32,IV33,IV34,IV35,IV36,IV37,IV38,IV39,IV40,IV41,IV42,IV43,IV44,IV45,IV46,IV47,IV48,IV49,IV50,IV51,IV52,IV53,IV54,IV55,IV56,IV57,IV58,IV59,IV60,IV61,IV62,IV63,KW1,KW2,KW3,KW4,KW5,KW6,KW7,KW8,KW9,KW10,KW11,KW12,KW13,KW14,KW15,KW16,KW17,PH15,PH16,PH17,PH18,PH19,PH20,PH21,PH22,PH23,PH24,PH25,PH26,PH27,PH28,PH29,PH30,PH31,PH32,PH33,PH34,PH35,PH36,PH37,PH38,PH39,PH40,PH41,PH42,PH43,PH44,PH45,PH46,PH47,PH48,PH49,PH50,PA20,PA21,PA22,PA23,PA24,PA25,PA26,PA27,PA28,PA29,PA30,PA31,PA32,PA33,PA34,PA35,PA36,PA37,PA38,PA39,PA40,PA41,PA42,PA43,PA44,PA45,PA46,PA47,PA48,PA49,PA50,PA51,PA52,PA53,PA54,PA55,PA56,PA57,PA58,PA59,PA60,PA61,PA62,PA63,PA64,PA65,PA66,PA67,PA68,PA69,PA70,PA71,PA72,PA73,PA74,PA75,PA76,PA77,PA78,HS1,HS2,HS3,HS4,HS5,HS6,HS7,HS8,HS9,ZE1,ZE2,ZE3,FK16,FK17,FK18,FK19,FK20,FK21,PO30,PO31,PO32,PO33,PO34,PO35,PO36,PO37,PO38,PO39,PO40,PO41,TR21,TR22,TR23,TR24,TR25,IM1,IM2,IM3,IM4,IM5,IM6,IM7,IM8,IM9,IM86,IM87,IM88,IM89,IM90,IM91,IM92,IM93,IM94,IM95,IM96,IM97,IM98,IM99,GY1,GY2,GY3,GY4,GY5,GY6,GY7,GY8,GY9,GY10,JE1,JE2,JE3,JE4,JE5,BT1,BT2,BT3,BT4,BT5,BT6,BT7,BT8,BT9,BT10,BT11,BT12,BT13,BT14,BT15,BT16,BT17,BT18,BT19,BT20,BT21,BT22,BT23,BT24,BT25,BT26,BT27,BT28,BT29,BT30,BT31,BT32,BT33,BT34,BT35,BT36,BT37,BT38,BT39,BT40,BT41,BT42,BT43,BT44,BT45,BT46,BT47,BT48,BT49,BT50,BT51,BT52,BT53,BT54,BT55,BT56,BT57,BT58,BT59,BT60,BT61,BT62,BT63,BT64,BT65,BT66,BT67,BT68,BT69,BT70,BT71,BT72,BT73,BT74,BT75,BT76,BT77,BT78,BT79,BT80,BT81,BT82,BT83,BT84,BT85,BT86,BT87,BT88,BT89,BT90,BT91,BT92,BT93,BT94";
  const postCodeArray = postCodes.split(",");
  const outwardCode = getOutcode(postcode);

  return postCodeArray.includes(outwardCode);
}

function valid_postcode(postcode) {
  postcode = postcode.replace(/\s/g, "");
  const regex = /[A-Z]{1,2}[A-Z0-9]{1,2} ?[0-9][A-Z]{2}/i;
  return regex.test(postcode);
}

function isDestinationNI(postcode) {
  const prefix = postcode.trim().slice(0, 2).toUpperCase();
  return ["BT", "IM", "GY", "JE"].includes(prefix);
}

// --- Shipping ---------------------------------------------------------------

// The line-item shape for a shipping product (GB or NI variant) added to the
// cart. Shared by ClearShipping (quantity 0) and ContinueToCheckout.
function buildShippingLineItem(variantId, quantity) {
  return {
    // Shopify's /cart/*.js endpoints require `id` as a string (the variant-id
    // constants are numeric literals), else /cart/change.js rejects it.
    id: String(variantId),
    quantity,
    properties: { "shipping code": SHIPPING_CODE_ND },
  };
}

function returnShippingCode(postcode, itemIndex) {
  if (longShippingTime(postcode)) {
    return "ND-23";
  }

  if (postcode.trim().substring(0, 2).toUpperCase() === "BT") {
    return "ND";
  }

  if (
    document
      .querySelector(".jq-address-" + itemIndex)
      .dataset.title.toLowerCase()
      .includes("penny post")
  ) {
    return "RM";
  }
  return "ND";
}

// --- Misc DOM / time helpers ------------------------------------------------

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function truncateButtonText(selector, maxLength = 20) {
  const btn = document.querySelector(selector);
  if (!btn) return;

  const originalText = btn.textContent;

  if (originalText.length > maxLength) {
    btn.textContent = originalText.substring(0, maxLength) + "...";
    btn.title = originalText; // Show full text on hover
  }
}

function addGiftMessageToAll(giftMessage) {
  document.querySelectorAll(".gift-button").forEach((btn) => {
    btn.value = giftMessage;
  });
}

// Show the "shipping" notice once the cart holds 10+ items (used by both the
// cart page and the cart drawer).
function DisplayShippingMessage() {
  const messageContainer = document.getElementById("drawer-shipping-message");
  if (!messageContainer) return;

  const totalQuantity = Array.from(document.querySelectorAll(".quantity__input"))
    .reduce((sum, input) => sum + parseInt(input.value, 10), 0);

  messageContainer.style.display = totalQuantity >= 10 ? "block" : "none";
}

function isAfter3PM_GMT1() {
  const now = new Date();
  const currentHour = parseInt(now.toLocaleTimeString("en-US", {
    timeZone: "Europe/Berlin",
    hour: 'numeric',
    hour12: false
  }));

  return currentHour > 15;
}